import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RolPlataforma } from '../../../../core/models/novedades.models';
import { Postulacion } from '../../../../core/models/postulaciones.models';
import { PatrocinioService } from '../../../../core/services/patrocinio.service';
import { PostulacionesLogService } from '../../../../core/services/postulaciones-log.service';
import { PostulacionesService } from '../../../../core/services/postulaciones.service';
import { RoleContextService } from '../../../../core/services/role-context.service';
import { etiquetaEstadoPostulacion } from '../../../../core/utils/postulaciones-filter.util';
import { routeForRole } from '../../../../core/utils/roles.util';
import { AccessibilityToolbar } from '../../../../shared/components/accessibility-toolbar/accessibility-toolbar';
import { BreadcrumbItem, Breadcrumbs } from '../../../../shared/components/breadcrumbs/breadcrumbs';
import { UserSessionSummary } from '../../../../shared/components/user-session-summary/user-session-summary';
import { SidebarMenu } from '../../../../shared/sidebar-menu/sidebar-menu';

@Component({
  selector: 'app-postulacion-detalle',
  imports: [CommonModule, SidebarMenu, AccessibilityToolbar, Breadcrumbs, UserSessionSummary],
  templateUrl: './postulacion-detalle.html',
  styleUrl: './postulacion-detalle.css',
})
export class PostulacionDetalle {
  rol: RolPlataforma = 'analista';
  idPostulacion = '';
  postulacion: Postulacion | null = null;
  mensaje = 'Cargando detalle de postulacion...';
  mensajeFirma = '';
  solicitarFirmaPatrocinio = false;
  origen = 'postulaciones';
  modalFirmaVisible = false;
  firmandoPatrocinio = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly roleContext: RoleContextService,
    private readonly postulacionesService: PostulacionesService,
    private readonly patrocinioService: PatrocinioService,
    private readonly postulacionesLogService: PostulacionesLogService
  ) {
    this.route.queryParamMap.subscribe((params) => {
      this.rol = this.roleContext.resolveRole(params, this.rol);
      this.origen = params.get('from') ?? 'postulaciones';
      this.solicitarFirmaPatrocinio = params.get('firma') === 'true';
      this.cargarDetalle();
    });

    this.route.paramMap.subscribe((params) => {
      this.idPostulacion = params.get('id') ?? '';
      this.cargarDetalle();
    });
  }

  get breadcrumbs(): BreadcrumbItem[] {
    if (this.esRolPatrocinio) {
      return [
        { label: 'Inicio', link: routeForRole(this.rol) },
        { label: 'Patrocinio', link: '/patrocinio' },
        { label: this.idPostulacion || 'Detalle' },
      ];
    }

    return [
      { label: 'Inicio', link: '/inicio' },
      { label: 'Postulaciones', link: '/postulaciones' },
      { label: this.idPostulacion || 'Detalle' },
    ];
  }

  get esRolPatrocinio(): boolean {
    return this.rol === 'representante_institucional' || this.rol === 'ayudante_representante';
  }

  get puedeFirmarPatrocinio(): boolean {
    return (
      this.rol === 'representante_institucional' &&
      !!this.postulacion &&
      this.postulacion.solicitudPatrocinio &&
      !this.postulacion.firmaPatrocinioRealizada &&
      this.postulacion.estadoInvitacion !== 'rechazada' &&
      this.postulacion.estadoInvitacion !== 'observada'
    );
  }

  get firmaPatrocinioRegistrada(): boolean {
    return this.postulacion?.firmaPatrocinioRealizada === true;
  }

  get mostrarSeccionPatrocinio(): boolean {
    return this.esRolPatrocinio && !!this.postulacion?.solicitudPatrocinio;
  }

  get etiquetaEstadoPatrocinio(): string {
    if (this.firmaPatrocinioRegistrada) return 'Firmada';
    if (this.postulacion?.estadoInvitacion === 'observada') return 'Observada';
    if (this.postulacion?.estadoInvitacion === 'rechazada') return 'Rechazada';
    return 'Solicitada';
  }

  get etiquetaEstadoPostulacionActual(): string {
    return this.postulacion ? etiquetaEstadoPostulacion(this.postulacion.estadoPostulacion) : '';
  }

  abrirModalFirma(): void {
    if (!this.puedeFirmarPatrocinio) return;
    this.mensajeFirma = '';
    this.modalFirmaVisible = true;
  }

  cerrarModalFirma(): void {
    if (this.firmandoPatrocinio) return;
    this.modalFirmaVisible = false;
  }

  confirmarFirmaPatrocinio(): void {
    if (!this.postulacion || !this.puedeFirmarPatrocinio) return;

    this.firmandoPatrocinio = true;
    this.mensajeFirma = '';

    this.patrocinioService.firmarPatrocinio(this.postulacion.idPostulacion, { rol: this.rol }).subscribe({
      next: (response) => {
        if (!this.postulacion) return;

        this.postulacion = this.patrocinioService.aplicarRespuesta(this.postulacion, response);
        this.postulacion = this.patrocinioService.aplicarEstadoPatrocinio([this.postulacion])[0] ?? this.postulacion;
        this.mensaje = 'La firma institucional fue registrada correctamente.';
        this.mensajeFirma = 'Patrocinio formalizado y registrado en el historial.';
        this.modalFirmaVisible = false;
        this.firmandoPatrocinio = false;

        this.postulacionesLogService.registrar({
          timestamp: new Date().toISOString(),
          rol: this.rol,
          idPostulacion: this.postulacion.idPostulacion,
          folio: this.postulacion.folio,
          accion: 'firmar_patrocinio',
          detalle: `Firma institucional registrada para la postulacion ${this.postulacion.idPostulacion}.`,
        });
      },
      error: () => {
        this.firmandoPatrocinio = false;
        this.mensajeFirma = 'No fue posible registrar la firma de patrocinio.';
      },
    });
  }

  volverAPatrocinio(): void {
    this.router.navigate(['/patrocinio'], { queryParams: { role: this.rol } });
  }

  private cargarDetalle(): void {
    if (!this.idPostulacion) return;

    this.postulacionesService.getMisPostulaciones().subscribe((items) => {
      const ajustadas = this.patrocinioService.aplicarEstadoPatrocinio(items);
      const visibles =
        this.rol === 'analista'
          ? ajustadas.filter((item) => item.asignadaAnalista)
          : this.rol === 'urc'
            ? ajustadas
            : this.esRolPatrocinio
              ? ajustadas.filter((item) => item.solicitudPatrocinio)
              : [];

      this.postulacion = visibles.find((item) => item.idPostulacion === this.idPostulacion) ?? null;

      if (!this.postulacion) {
        this.mensaje = 'No fue posible encontrar la postulacion solicitada.';
        return;
      }

      if (this.puedeFirmarPatrocinio && this.solicitarFirmaPatrocinio) {
        this.mensaje = 'Revisa el detalle de la postulacion y formaliza la firma de patrocinio.';
        return;
      }

      if (this.firmaPatrocinioRegistrada && this.esRolPatrocinio) {
        this.mensaje = 'La firma institucional ya fue registrada para esta postulacion.';
        return;
      }

      this.mensaje = this.esRolPatrocinio
        ? 'Visualizacion del detalle de patrocinio en modo lectura.'
        : 'Visualizacion en solo lectura del detalle de la postulacion.';
    });
  }
}
